/**
 * Elite assault brute skills — brass cone + leap; ironhorn also yellow line charge.
 * Phaser-free. Numbers from assault.json bruteSkills / bruteToughness.
 * Entrance drop: combatArmed false until dropRemain elapses.
 *
 * Ironhorn charge (`bruteSkills.charge`) must stay strictly stronger than
 * W3 `chargerSkills.charge` (damage / range / halfWidth / knock) — see assault.json note.
 */

import { raidCatalog } from "../../data/raid-catalog";
import { clampPointToRect, noteHit } from "../../sim";
import { assaultScript } from "./raid-assault";
import { tryEmitBruteBark } from "./raid-bark-emit";
import { bruteVariantLookForUnit } from "./raid-brute-variant";
import { deferRaidSkillHit } from "./raid-deferred-hits";
import { RaidEvent } from "./raid-events";
import { pointInTelegraph } from "./raid-ai";
import type { RaidBattleState, RaidUnit } from "./raid-types";
import { raidDangerColor } from "./raid-threat";
import { getActivePlayBounds } from "./sim-lab/sim-stage";
import { RaidEnemyDefId } from "./raid-enemy";
import { scaleEnemyOutgoingDamage } from "./raid-escalate";

/** Brutes with leap/brass hit queued this tick (zone stays until flush). */
const deferredLeapLand = new Set<number>();
const deferredBrassResolve = new Set<number>();

export function isRaidBrute(u: RaidUnit): boolean {
  return u.defId === RaidEnemyDefId.Brute && u.team === 1;
}

export function livingBrutes(state: RaidBattleState): RaidUnit[] {
  return state.units.filter((u) => isRaidBrute(u) && u.hp > 0);
}

/** Armed living brutes (post entrance drop) — HUD focus / skills / targets. */
export function livingArmedBrutes(state: RaidBattleState): RaidUnit[] {
  return livingBrutes(state).filter((u) => u.combatArmed);
}

function playBounds() {
  return getActivePlayBounds();
}

function clampUnit(u: RaidUnit, x: number, y: number): { x: number; y: number } {
  return clampPointToRect(x, y, playBounds(), u.radius);
}

function dist(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(ax - bx, ay - by);
}

/** Match line DangerZone SSOT (raid-threat pointInDangerZone). */
function pointInChargeCorridor(
  x: number,
  y: number,
  unitRadius: number,
  ox: number,
  oy: number,
  aim: number,
  len: number,
  halfWidth: number,
): boolean {
  const hw = Math.max(1, halfWidth) + unitRadius;
  const dx = x - ox;
  const dy = y - oy;
  const fx = Math.cos(aim);
  const fy = Math.sin(aim);
  const along = dx * fx + dy * fy;
  if (along < -unitRadius || along > len + unitRadius) return false;
  const px = dx - fx * along;
  const py = dy - fy * along;
  return Math.hypot(px, py) <= hw;
}

function distPointToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const ab2 = abx * abx + aby * aby;
  if (ab2 < 1e-8) return Math.hypot(apx, apy);
  let t = (apx * abx + apy * aby) / ab2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + abx * t), py - (ay + aby * t));
}

function nearestAlly(state: RaidBattleState, from: RaidUnit): RaidUnit | null {
  let best: RaidUnit | null = null;
  let bestD = Infinity;
  for (const u of state.units) {
    if (u.team !== 0 || u.hp <= 0) continue;
    const d = dist(from.x, from.y, u.x, u.y);
    if (d < bestD) {
      bestD = d;
      best = u;
    }
  }
  return best;
}

function isIronhornBrute(state: RaidBattleState, brute: RaidUnit): boolean {
  return bruteVariantLookForUnit(state.units, brute.id).id === "ironhorn";
}

function beginSimpleKnock(
  u: RaidUnit,
  fromX: number,
  fromY: number,
  knockDist: number,
  dur: number,
  apex: number,
  recoverSec: number,
  aimAngle?: number,
): void {
  let nx = u.x - fromX;
  let ny = u.y - fromY;
  let len = Math.hypot(nx, ny);
  if (len < 1e-3 && aimAngle != null) {
    nx = Math.cos(aimAngle);
    ny = Math.sin(aimAngle);
    len = 1;
  } else if (len < 1e-3) {
    nx = 0;
    ny = 1;
    len = 1;
  }
  nx /= len;
  ny /= len;
  const start = clampUnit(u, u.x, u.y);
  const end = clampUnit(
    u,
    start.x + nx * knockDist,
    start.y + ny * knockDist,
  );
  u.x = start.x;
  u.y = start.y;
  u.vx = 0;
  u.vy = 0;
  u.intentVx = 0;
  u.intentVy = 0;
  u.knock = {
    fromX: start.x,
    fromY: start.y,
    toX: end.x,
    toY: end.y,
    apex: Math.max(0, apex),
    dur: Math.max(0.05, dur),
    t: 0,
    recoverSec: Math.max(0, recoverSec),
  };
}

function finalizeUnitDead(state: RaidBattleState, u: RaidUnit): void {
  u.hp = 0;
  u.vx = 0;
  u.vy = 0;
  u.intentVx = 0;
  u.intentVy = 0;
  u.attackAnimT = 0;
  u.attackCdLeft = 1e9;
  u.knock = null;
  state.events.push({ kind: RaidEvent.Death, unitId: u.id, team: u.team });
}

/** Apply toughness chip from an ally hit on a living armed brute. */
export function applyBruteToughnessFromHit(
  state: RaidBattleState,
  brute: RaidUnit,
  from: RaidUnit,
): void {
  if (!isRaidBrute(brute) || brute.hp <= 0 || !brute.combatArmed) return;
  if (state.time < brute.stunnedUntil) return;
  const combat = raidCatalog.balance.combat;
  const base =
    combat.toughnessDamage[from.classId] ?? combat.toughnessDamage.dps;
  const windowOpen =
    brute.telegraph != null || brute.leap?.phase === "windup";
  const mul =
    from.classId === "tank" && windowOpen ? combat.interruptWindowMul : 1;
  const chip = base * mul;
  if (chip <= 0 || brute.toughness <= 0) return;
  brute.toughness = Math.max(0, brute.toughness - chip);
  if (brute.toughness <= 0) triggerBruteBreak(state, brute);
}

export function triggerBruteBreak(
  state: RaidBattleState,
  brute: RaidUnit,
): void {
  const script = assaultScript(state.encounter.scriptId);
  const stunSec = script.bruteToughness.breakStunSec;
  brute.toughness = 0;
  brute.telegraph = null;
  brute.leap = null;
  brute.intentVx = 0;
  brute.intentVy = 0;
  brute.vx = 0;
  brute.vy = 0;
  brute.breakStunDuration = stunSec;
  brute.stunnedUntil = state.time + stunSec;
  chargeHitIds.delete(brute.id);
  state.events.push({ kind: RaidEvent.Break, x: brute.x, y: brute.y });
  tryEmitBruteBark(state, brute, "break");
}

/**
 * Apply HP damage to a brute (break mul when stunned). Returns true if dead.
 * No-op while entrance-dropping (!combatArmed).
 */
export function damageBrute(
  state: RaidBattleState,
  brute: RaidUnit,
  amount: number,
  from?: RaidUnit,
  skillId?: string,
): boolean {
  if (!isRaidBrute(brute) || brute.hp <= 0 || amount <= 0) return false;
  if (!brute.combatArmed) return false;
  if (from && from.hp <= 0) return false;
  const combat = raidCatalog.balance.combat;
  let dmg = amount;
  if (state.time < brute.stunnedUntil) {
    dmg *= combat.breakHpDamageMul ?? 2;
  }
  brute.hp -= dmg;
  if (from) {
    noteHit(brute.hit, dmg, from.id, state.time);
    state.events.push({
      kind: RaidEvent.Hit,
      x0: from.x,
      y0: from.y,
      x1: brute.x,
      y1: brute.y,
      damage: dmg,
      attackerId: from.id,
      targetKind: "unit",
      targetId: brute.id,
      ...(skillId ? { skillId } : {}),
    });
    applyBruteToughnessFromHit(state, brute, from);
  }
  if (brute.hp <= 0) {
    finalizeUnitDead(state, brute);
    return true;
  }
  return false;
}

function resolveBruteLeapLand(state: RaidBattleState, brute: RaidUnit): void {
  const leap = brute.leap;
  if (!leap) return;
  const script = assaultScript(state.encounter.scriptId);
  const skills = script.bruteSkills;
  const combat = raidCatalog.balance.combat;
  // Hit volume SSOT = telegraph land (DangerZone). Clamp pose after resolve so
  // walls never slide the disk onto feet that already cleared the paint.
  const landX = leap.landX;
  const landY = leap.landY;
  const landRadius = leap.landRadius;
  const landDamage = leap.landDamage;
  const landDanger = leap.danger;
  // Keep leap zone through ally motion this tick; apply hits after tickUnits.
  if (deferredLeapLand.has(brute.id)) return;
  deferredLeapLand.add(brute.id);
  deferRaidSkillHit(() => {
    deferredLeapLand.delete(brute.id);
    let hits = 0;
    const dmg = scaleEnemyOutgoingDamage(state, landDamage);
    for (const u of state.units) {
      if (u.team !== 0 || u.hp <= 0) continue;
      if (dist(landX, landY, u.x, u.y) > landRadius + u.radius) continue;
      u.hp -= dmg;
      noteHit(u.hit, dmg, brute.id, state.time);
      hits += 1;
      beginSimpleKnock(
        u,
        landX,
        landY,
        combat.leapKnockDist ?? combat.leapKnockback ?? 96,
        combat.leapFlightSec ?? 0.28,
        combat.leapKnockApex ?? 56,
        0,
      );
      u.stunnedUntil = Math.max(
        u.stunnedUntil,
        state.time + (combat.leapLandStunSec ?? 1.65),
      );
      state.events.push({
        kind: RaidEvent.Hit,
        x0: landX,
        y0: landY,
        x1: u.knock?.toX ?? u.x,
        y1: u.knock?.toY ?? u.y,
        damage: dmg,
        attackerId: brute.id,
        targetKind: "unit",
        targetId: u.id,
        skillId: "leap",
      });
      if (u.hp <= 0) finalizeUnitDead(state, u);
    }
    state.events.push({
      kind: RaidEvent.LeapLand,
      x: landX,
      y: landY,
      hits,
      danger: landDanger,
      radius: landRadius,
    });
    const p = clampUnit(brute, landX, landY);
    brute.x = p.x;
    brute.y = p.y;
    brute.leap = null;
    brute.leapCd = skills.leap.cooldownSec;
    brute.intentVx = 0;
    brute.intentVy = 0;
    brute.vx = 0;
    brute.vy = 0;
  });
}

/** Per-charge hit de-dupe (brute id → allies already struck this dash). */
const chargeHitIds = new Map<number, Set<number>>();

/**
 * Ironhorn charge hits must match the line DangerZone corridor (+ swept body
 * this tick). A bare body disk around the brute hits off-corridor feet that
 * correctly left the painted volume — “dodge then die” with inZone=false.
 */
function hitAlliesAlongIronhornCharge(
  state: RaidBattleState,
  brute: RaidUnit,
  halfWidth: number,
  damage: number,
  aimAngle: number,
  hitIds: Set<number>,
  knock: {
    knockDist: number;
    knockFlightSec: number;
    knockApex: number;
    hitStunSec: number;
  },
  fromX: number,
  fromY: number,
  corridorOx: number,
  corridorOy: number,
  corridorLen: number,
): number {
  let hits = 0;
  for (const ally of state.units) {
    if (ally.team !== 0 || ally.hp <= 0) continue;
    if (hitIds.has(ally.id)) continue;
    if (
      !pointInChargeCorridor(
        ally.x,
        ally.y,
        ally.radius,
        corridorOx,
        corridorOy,
        aimAngle,
        corridorLen,
        halfWidth,
      )
    ) {
      continue;
    }
    const dSeg = distPointToSegment(
      ally.x,
      ally.y,
      fromX,
      fromY,
      brute.x,
      brute.y,
    );
    if (dSeg > halfWidth + ally.radius) continue;
    hitIds.add(ally.id);
    const dmg = scaleEnemyOutgoingDamage(state, damage);
    ally.hp -= dmg;
    noteHit(ally.hit, dmg, brute.id, state.time);
    hits += 1;
    beginSimpleKnock(
      ally,
      brute.x,
      brute.y,
      knock.knockDist,
      knock.knockFlightSec,
      knock.knockApex,
      knock.hitStunSec,
      aimAngle,
    );
    state.events.push({
      kind: RaidEvent.Hit,
      x0: brute.x,
      y0: brute.y,
      x1: ally.knock?.toX ?? ally.x,
      y1: ally.knock?.toY ?? ally.y,
      damage: dmg,
      attackerId: brute.id,
      targetKind: "unit",
      targetId: ally.id,
      skillId: "charge",
    });
    if (ally.hp <= 0) finalizeUnitDead(state, ally);
  }
  return hits;
}

function tickIronhornChargeTravel(
  state: RaidBattleState,
  brute: RaidUnit,
  dt: number,
): boolean {
  const leap = brute.leap;
  if (!leap || leap.purpose !== "charge") return false;
  const script = assaultScript(state.encounter.scriptId);
  const charge = script.bruteSkills.charge;
  if (!charge) return false;

  leap.remain -= dt;
  if (leap.phase === "windup") {
    brute.intentVx = 0;
    brute.intentVy = 0;
    brute.vx = 0;
    brute.vy = 0;
    if (brute.telegraph) {
      // Sync remain only — open Telegraph cue is once at cast (line-fill overlay).
      brute.telegraph.remain = leap.remain;
    }
    if (leap.remain <= 1e-6) {
      // Keep line telegraph through travel (same as charger — no mid-dash despawn).
      leap.phase = "travel";
      leap.remain = charge.travelSec;
      leap.duration = charge.travelSec;
      leap.fromX = brute.x;
      leap.fromY = brute.y;
      // remain=0 ⇒ telegraphFillProgress=1 (full corridor through resolve).
      if (brute.telegraph) {
        brute.telegraph.remain = 0;
      }
      chargeHitIds.set(brute.id, new Set());
    }
    return true;
  }

  if (brute.telegraph) {
    brute.telegraph.remain = 0;
  }

  const travelDur = Math.max(0.05, leap.duration);
  const t = 1 - Math.max(0, leap.remain) / travelDur;
  const u = Math.min(1, Math.max(0, t));
  const e = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
  const prevX = brute.x;
  const prevY = brute.y;
  brute.x = leap.fromX + (leap.landX - leap.fromX) * e;
  brute.y = leap.fromY + (leap.landY - leap.fromY) * e;
  const aim = Math.atan2(leap.landY - leap.fromY, leap.landX - leap.fromX);
  const corridorLen = Math.max(1, dist(leap.fromX, leap.fromY, leap.landX, leap.landY));
  let hitBag = chargeHitIds.get(brute.id);
  if (!hitBag) {
    hitBag = new Set();
    chargeHitIds.set(brute.id, hitBag);
  }
  // Sweep geometry frozen this tick; ally feet sampled after tickUnits (deferred).
  const endX = brute.x;
  const endY = brute.y;
  const corridorOx = leap.fromX;
  const corridorOy = leap.fromY;
  const knock = {
    knockDist: charge.knockDist,
    knockFlightSec: charge.knockFlightSec,
    knockApex: charge.knockApex,
    hitStunSec: charge.hitStunSec,
  };
  deferRaidSkillHit(() => {
    const bx = brute.x;
    const by = brute.y;
    brute.x = endX;
    brute.y = endY;
    hitAlliesAlongIronhornCharge(
      state,
      brute,
      charge.halfWidth,
      charge.damage,
      aim,
      hitBag,
      knock,
      prevX,
      prevY,
      corridorOx,
      corridorOy,
      corridorLen,
    );
    brute.x = bx;
    brute.y = by;
  });
  if (leap.remain <= 1e-6) {
    // Keep charge DangerZone through ally motion; clear after deferred hits.
    deferRaidSkillHit(() => {
      const p = clampUnit(brute, leap.landX, leap.landY);
      brute.x = p.x;
      brute.y = p.y;
      brute.leap = null;
      brute.telegraph = null;
      brute.chargeCd = charge.cooldownSec;
      brute.intentVx = 0;
      brute.intentVy = 0;
      brute.vx = 0;
      brute.vy = 0;
      chargeHitIds.delete(brute.id);
      // No LeapLand — ironhorn charge matches charger: path corridor only.
    });
  }
  return true;
}

function tryBeginIronhornCharge(
  state: RaidBattleState,
  brute: RaidUnit,
  nearest: RaidUnit,
): boolean {
  if (!isIronhornBrute(state, brute)) return false;
  const script = assaultScript(state.encounter.scriptId);
  const charge = script.bruteSkills.charge;
  if (!charge || brute.chargeCd > 0) return false;
  const range = charge.range;
  const d = dist(brute.x, brute.y, nearest.x, nearest.y);
  // Start when target is inside charge corridor reach (same gate as brass-ish).
  if (d > range + nearest.radius) return false;

  const aim = Math.atan2(nearest.y - brute.y, nearest.x - brute.x);
  const land = clampUnit(
    brute,
    brute.x + Math.cos(aim) * range,
    brute.y + Math.sin(aim) * range,
  );
  const windup = charge.windupSec;
  const landDist = Math.max(1, dist(brute.x, brute.y, land.x, land.y));
  // Same form as charger: line corridor fill (not cone / tip circle).
  brute.telegraph = {
    shape: "line",
    range: landDist,
    coneDeg: 0,
    aimAngle: aim,
    remain: windup,
    duration: windup,
    windupSec: windup,
    damage: charge.damage,
    danger: raidDangerColor(charge.danger),
    skillId: "charge",
    halfWidth: charge.halfWidth,
    originPad: 0,
  };
  brute.leap = {
    phase: "windup",
    remain: windup,
    duration: windup,
    landX: land.x,
    landY: land.y,
    fromX: brute.x,
    fromY: brute.y,
    landRadius: charge.halfWidth,
    landDamage: charge.damage,
    danger: raidDangerColor(charge.danger),
    purpose: "charge",
  };
  brute.intentVx = 0;
  brute.intentVy = 0;
  brute.vx = 0;
  brute.vy = 0;
  brute.aimX = land.x;
  brute.aimY = land.y;
  // Emit open telegraph once (presentation keys on remain≈duration).
  state.events.push({
    kind: RaidEvent.Telegraph,
    shape: "line",
    x: brute.x,
    y: brute.y,
    range: landDist,
    coneDeg: 0,
    aimAngle: aim,
    remain: windup,
    duration: windup,
    danger: raidDangerColor(charge.danger),
    skillId: "charge",
    halfWidth: charge.halfWidth,
    originPad: 0,
  });
  tryEmitBruteBark(state, brute, "telegraph");
  return true;
}

function tickOneBrute(
  state: RaidBattleState,
  brute: RaidUnit,
  dt: number,
): void {
  const script = assaultScript(state.encounter.scriptId);
  const skills = script.bruteSkills;
  const combat = raidCatalog.balance.combat;

  // Entrance drop — land pose, no skills/AA until armed.
  if (!brute.combatArmed) {
    brute.dropRemain = Math.max(0, brute.dropRemain - dt);
    brute.vx = 0;
    brute.vy = 0;
    brute.intentVx = 0;
    brute.intentVy = 0;
    brute.telegraph = null;
    brute.leap = null;
    chargeHitIds.delete(brute.id);
    if (brute.dropRemain <= 1e-6) {
      brute.combatArmed = true;
      brute.dropRemain = 0;
    }
    return;
  }

  // Stun end → restore toughness
  if (brute.stunnedUntil > 0 && state.time >= brute.stunnedUntil) {
    brute.stunnedUntil = 0;
    brute.breakStunDuration = 0;
    brute.toughness = brute.toughnessMax;
  }

  if (state.time < brute.stunnedUntil) {
    brute.telegraph = null;
    brute.leap = null;
    chargeHitIds.delete(brute.id);
    brute.intentVx = 0;
    brute.intentVy = 0;
    brute.vx = 0;
    brute.vy = 0;
    return;
  }

  brute.brassCd = Math.max(0, brute.brassCd - dt);
  brute.leapCd = Math.max(0, brute.leapCd - dt);
  brute.chargeCd = Math.max(0, brute.chargeCd - dt);

  if (tickIronhornChargeTravel(state, brute, dt)) {
    return;
  }

  if (brute.leap) {
    const leap = brute.leap;
    leap.remain -= dt;
    if (leap.phase === "windup") {
      brute.intentVx = 0;
      brute.intentVy = 0;
      brute.vx = 0;
      brute.vy = 0;
      if (leap.remain <= 1e-6) {
        leap.phase = "travel";
        leap.remain = skills.leap.travelSec;
        leap.duration = skills.leap.travelSec;
        leap.fromX = brute.x;
        leap.fromY = brute.y;
      }
      return;
    }
    const travelDur = Math.max(0.05, leap.duration);
    const t = 1 - Math.max(0, leap.remain) / travelDur;
    const u = Math.min(1, Math.max(0, t));
    const e = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
    brute.x = leap.fromX + (leap.landX - leap.fromX) * e;
    brute.y = leap.fromY + (leap.landY - leap.fromY) * e;
    if (leap.remain <= 1e-6) {
      resolveBruteLeapLand(state, brute);
    }
    return;
  }

  if (brute.telegraph) {
    brute.intentVx = 0;
    brute.intentVy = 0;
    brute.vx = 0;
    brute.vy = 0;
    brute.telegraph.remain -= dt;
    state.events.push({
      kind: RaidEvent.Telegraph,
      shape: brute.telegraph.shape,
      x: brute.x,
      y: brute.y,
      range: brute.telegraph.range,
      coneDeg: brute.telegraph.coneDeg,
      aimAngle: brute.telegraph.aimAngle,
      remain: brute.telegraph.remain,
      duration: brute.telegraph.duration,
      danger: brute.telegraph.danger,
      skillId: brute.telegraph.skillId,
    });
    if (brute.telegraph.remain <= 0) {
      const tg = brute.telegraph;
      // Keep brass cone through ally motion this tick; apply after tickUnits.
      if (!deferredBrassResolve.has(brute.id)) {
        deferredBrassResolve.add(brute.id);
        const ox = brute.x;
        const oy = brute.y;
        deferRaidSkillHit(() => {
          deferredBrassResolve.delete(brute.id);
          let hits = 0;
          const brassDmg = scaleEnemyOutgoingDamage(state, tg.damage);
          for (const ally of state.units) {
            if (ally.team !== 0 || ally.hp <= 0) continue;
            if (!pointInTelegraph(ally.x, ally.y, brute)) continue;
            ally.hp -= brassDmg;
            noteHit(ally.hit, brassDmg, brute.id, state.time);
            hits += 1;
            beginSimpleKnock(
              ally,
              ox,
              oy,
              combat.brassKnockDist ?? 40,
              combat.brassFlightSec ?? 0.11,
              combat.brassKnockApex ?? 16,
              combat.brassHitStunSec ?? 1.26,
              tg.aimAngle,
            );
            state.events.push({
              kind: RaidEvent.Hit,
              x0: ox,
              y0: oy,
              x1: ally.knock?.toX ?? ally.x,
              y1: ally.knock?.toY ?? ally.y,
              damage: brassDmg,
              attackerId: brute.id,
              targetKind: "unit",
              targetId: ally.id,
              skillId: tg.skillId,
            });
            if (ally.hp <= 0) finalizeUnitDead(state, ally);
          }
          state.events.push({
            kind: RaidEvent.BrassResolve,
            x: ox,
            y: oy,
            hits,
            danger: tg.danger,
            skillId: tg.skillId,
            range: tg.range,
            coneDeg: tg.coneDeg,
            aimAngle: tg.aimAngle,
          });
          brute.telegraph = null;
          brute.brassCd = skills.brass.cooldownSec;
        });
      }
    }
    return;
  }

  const nearest = nearestAlly(state, brute);
  if (!nearest) return;

  // Ironhorn: yellow line charge before leap/brass when in range.
  if (tryBeginIronhornCharge(state, brute, nearest)) {
    return;
  }

  if (brute.leapCd <= 0) {
    const land = clampUnit(brute, nearest.x, nearest.y);
    brute.leap = {
      phase: "windup",
      remain: skills.leap.windupSec,
      duration: skills.leap.windupSec,
      landX: land.x,
      landY: land.y,
      fromX: brute.x,
      fromY: brute.y,
      landRadius: skills.leap.landRadius,
      landDamage: skills.leap.landDamage,
      danger: raidDangerColor(skills.leap.danger),
      purpose: "combat",
    };
    brute.intentVx = 0;
    brute.intentVy = 0;
    brute.vx = 0;
    brute.vy = 0;
    tryEmitBruteBark(state, brute, "leap");
    return;
  }

  if (brute.brassCd <= 0) {
    const d = dist(brute.x, brute.y, nearest.x, nearest.y);
    if (d <= skills.brass.range + nearest.radius) {
      const aim = Math.atan2(nearest.y - brute.y, nearest.x - brute.x);
      brute.telegraph = {
        shape: "cone",
        range: skills.brass.range,
        coneDeg: skills.brass.coneDeg,
        aimAngle: aim,
        remain: skills.brass.windupSec + skills.brass.activeSec,
        duration: skills.brass.windupSec + skills.brass.activeSec,
        windupSec: skills.brass.windupSec,
        damage: skills.brass.damage,
        danger: raidDangerColor(skills.brass.danger),
        skillId: "brass",
      };
      brute.intentVx = 0;
      brute.intentVy = 0;
      brute.vx = 0;
      brute.vy = 0;
      tryEmitBruteBark(state, brute, "telegraph");
    }
  }
}

/** Tick entrance drop + brass/leap/(ironhorn charge) for living assault brutes. */
export function tickBruteSkills(state: RaidBattleState, dt: number): void {
  for (const u of state.units) {
    if (!isRaidBrute(u) || u.hp <= 0) continue;
    // Entrance drop must always progress — knock must not softlock combatArmed.
    if (!u.combatArmed) {
      tickOneBrute(state, u, dt);
      continue;
    }
    if (u.knock) continue;
    tickOneBrute(state, u, dt);
  }
}
